
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import QUERIES from '@/queries/queries'
import {
  DesignPackageLabelsFragment,
  ProjectsWithDesignPackagesDocument,
  ProjectsWithDesignPackagesQuery,
  ProjectsWithDesignPackagesQueryVariables,
  ProjectWithDesignPackagesFragment,
  RoomWithDesignPackagesFragment
} from '@/components/designPackages/gql/__generated__/getProjectsWithDesignPackages.query'
import {
  DesignPackageNameFragment,
  GetDesignPackageNamesDocument,
  GetDesignPackageNamesQuery,
  GetDesignPackageNamesQueryVariables
} from '@/components/designPackages/gql/__generated__/getDesignPackageNames.query'
import {
  CopyDesignPackageToRoomsDocument,
  CopyDesignPackageToRoomsMutation,
  CopyDesignPackageToRoomsMutationVariables
} from '@/components/designPackages/gql/__generated__/copyDesignPackageToRooms.mutation'
import {
  DesignPackagesForProjectDocument,
  DesignPackagesForProjectQuery
} from './gql/__generated__/getDesignPackagesForProject.query'

@Component
export default class ImportDesignPackageModal extends Vue {
  @Prop({ default: false, required: true }) show!: boolean
  @Prop({ default: () => [] }) productPackages!: { id: string; name: string }[]
  @Prop({ default: '' }) projectId!: string

  projectsWithDesignPackages: ProjectWithDesignPackagesFragment[] = []
  designPackageIds: string[] = []
  designPackageNames: DesignPackageNameFragment[] = []

  selectedProject: ProjectWithDesignPackagesFragment | null = null
  selectedSourceRoom: RoomWithDesignPackagesFragment | null = null
  selectedDesignPackage: DesignPackageLabelsFragment | null = null

  targetRoomIds: string[] = []

  isLoading = false

  created() {
    this.$apollo.addSmartQuery<ProjectsWithDesignPackagesQuery, ProjectsWithDesignPackagesQueryVariables>(
      QUERIES.ProjectsWithDesignPackages,
      {
        query: ProjectsWithDesignPackagesDocument,
        variables: () => ({
          productPackageId: this.productPackageId
        }),
        // Importing a design package updates the Apollo cache. Because of normalization, the cache data of this query
        // will be linked to the data of the import mutation. This query then sees the change, and re-evaluates itself.
        // This ensure the UI is updated without a need for refetching.
        fetchPolicy: 'cache-first',
        update: (data) => data,
        result: (result) => {
          this.projectsWithDesignPackages = result.data?.allProjects ?? []
          this.designPackageIds = this.projectsWithDesignPackages.flatMap((project) =>
            project.rooms.flatMap((room) => room.designPackages.map((dp) => dp.id))
          )
        },
        skip: () => !this.productPackageId,
        error: (error) => {
          this.$store.dispatch('showErrorDialog', {
            Code: 'E4250',
            Message: error
          })
        }
      }
    )

    this.$apollo.addSmartQuery<GetDesignPackageNamesQuery, GetDesignPackageNamesQueryVariables>(
      QUERIES.GetDesignPackageNamesQuery,
      {
        query: GetDesignPackageNamesDocument,
        variables: () => ({
          designPackageIds: this.designPackageIds
        }),
        update: (data) => data,
        result: (result) => {
          this.designPackageNames = (result.data.designPackagePage || []).filter(
            (x) => !!x
          ) as DesignPackageNameFragment[]
        },
        skip: () => !this.designPackageIds.length
      }
    )
  }

  get productPackageId() {
    // This works for product proposition projects because they can only have one product package assigned
    return this.productPackages.length > 0 ? this.productPackages[0].id : null
  }

  @Watch('selectedProject')
  onSelectedProjectChanged(
    newValue: ProjectWithDesignPackagesFragment | null,
    oldValue: ProjectWithDesignPackagesFragment | null
  ) {
    if (!newValue || newValue.id !== oldValue?.id) {
      this.selectedSourceRoom = null
    }
  }

  @Watch('selectedSourceRoom')
  onSelectedRoomChanged(
    newValue: RoomWithDesignPackagesFragment | null,
    oldValue: RoomWithDesignPackagesFragment | null
  ) {
    if (!newValue || newValue.id !== oldValue?.id) {
      this.selectedDesignPackage = null
    }
  }

  @Watch('selectedDesignPackage')
  onSelectedDesignPackageChanged(
    newValue: DesignPackageLabelsFragment | null,
    oldValue: DesignPackageLabelsFragment | null
  ) {
    if (!newValue || newValue.id !== oldValue?.id) {
      this.targetRoomIds = []
    }
  }

  get sourceRooms(): RoomWithDesignPackagesFragment[] {
    return this.selectedProject ? this.selectedProject.rooms : []
  }

  getLayoutsForRoom(room: RoomWithDesignPackagesFragment) {
    return room.layoutRooms.map((lr) => lr.layout.name).join(', ')
  }

  get designPackages(): DesignPackageLabelsFragment[] {
    return this.selectedSourceRoom
      ? this.selectedSourceRoom.designPackages.map((dp) => ({
          ...dp,
          name: this.designPackageNames.find((x) => x.alias?.alias === dp.id)?.displayName || dp.name
        }))
      : []
  }

  getDesignPackageLabels(designPackage: DesignPackageLabelsFragment) {
    const labels: string[] = []
    if (designPackage.isStandard) {
      labels.push(this.$t('projectDesignPackage.labels.standard').toString())
    }
    if (designPackage.hasBath) {
      labels.push(this.$t('projectDesignPackage.labels.bath').toString())
    }
    if (designPackage.hasToiletLabel) {
      labels.push(this.$t('projectDesignPackage.labels.toilet').toString())
    }
    if (designPackage.isXL) {
      labels.push(this.$t('projectDesignPackage.labels.xl').toString())
    }
    return labels.join(', ')
  }

  hasExistingDesignPackageWithLabelCombination(room: RoomWithDesignPackagesFragment) {
    return room.designPackages.some((dp) => {
      return this.selectedDesignPackage
        ? dp.isStandard === this.selectedDesignPackage.isStandard &&
            dp.hasBath === this.selectedDesignPackage.hasBath &&
            dp.hasToiletLabel === this.selectedDesignPackage.hasToiletLabel &&
            dp.isXL === this.selectedDesignPackage.isXL
        : false
    })
  }

  get project(): ProjectWithDesignPackagesFragment | undefined {
    return this.projectsWithDesignPackages.find((p) => p.id === this.projectId)
  }

  get targetRooms(): (RoomWithDesignPackagesFragment & { disabled: boolean })[] {
    return this.project
      ? this.project.rooms.map((room) => ({
          ...room,
          disabled: this.hasExistingDesignPackageWithLabelCombination(room)
        }))
      : []
  }

  close() {
    this.$emit('close')
    this.selectedProject = null
    this.selectedSourceRoom = null
    this.selectedDesignPackage = null
    this.targetRoomIds = []
  }

  async importDesignPackage() {
    if (!this.selectedDesignPackage || !this.targetRoomIds.length) return

    try {
      this.isLoading = true

      const result = await this.$apolloMutate<
        CopyDesignPackageToRoomsMutation,
        CopyDesignPackageToRoomsMutationVariables
      >({
        mutation: CopyDesignPackageToRoomsDocument,
        variables: {
          designPackageId: this.selectedDesignPackage.id,
          roomIds: this.targetRoomIds
        },
        update: (cache, result) => {
          // Because we are not using refetchQueries with the mutation, we need to update the Apollo cache manually to let it know that
          // newly created design packages should be added to the existing getDesignPackagesForProject query cache data. Then, the active
          // query in the parent ListDesignPackagesComponent sees the change and updates the UI based on cache state.
          // If we were not to do this, and a newly copied design package is created and removed in the same session, the list of
          // design packages would still show the deleted design package.

          const copiedDesignPackages = result.data?.copyDesignPackageToRooms || []
          if (!copiedDesignPackages.length) {
            return
          }

          const cachedData = cache.readQuery<DesignPackagesForProjectQuery>({
            query: DesignPackagesForProjectDocument,
            variables: { projectId: this.projectId }
          })

          if (cachedData) {
            // Apollo advices against editing the cache in-place, so make a deep copy first
            const clonedData = structuredClone(cachedData)
            copiedDesignPackages.forEach((cdp) => {
              clonedData.project?.rooms.find((r) => r.id === cdp.room.id)?.designPackages.push(cdp)
            })

            cache.writeQuery<DesignPackagesForProjectQuery>({
              query: DesignPackagesForProjectDocument,
              variables: { projectId: this.projectId },
              data: clonedData
            })
          }
        },
        error: 'E4251'
      })

      if (result) {
        this.close()
      }
    } finally {
      this.isLoading = false
    }
  }
}
